// src/firebase.js
import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithRedirect,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getMessaging, isSupported } from "firebase/messaging";

// ✅ Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyD9iIfUOazmT6bzeBpsSO8cF5yNanD_RDg",
  authDomain: "greek-ranking.firebaseapp.com",
  projectId: "greek-ranking",
  storageBucket: "greek-ranking.firebasestorage.app",
  messagingSenderId: "349112505086",
  appId: "1:349112505086:web:40c6ea3063b468646f0baf"
};

const app = initializeApp(firebaseConfig);

// 🔐 Auth, Firestore, Storage, Messaging
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const provider = new GoogleAuthProvider();

// 🔒 Set auth persistence to LOCAL (keeps you logged in across browser sessions)
const initAuthPersistence = async () => {
  try {
    await setPersistence(auth, indexedDBLocalPersistence);
    console.log('✅ Auth persistence set to IndexedDB (best for mobile)');
  } catch (error) {
    try {
      await setPersistence(auth, browserLocalPersistence);
      console.log('✅ Auth persistence set to localStorage');
    } catch (fallbackError) {
      console.warn('⚠️ Could not set auth persistence:', fallbackError);
    }
  }
};

initAuthPersistence();

// 🔄 Proactive token refresh to prevent logout on network/IP changes
const setupTokenRefresh = () => {
  // Refresh token when app comes back online
  window.addEventListener('online', async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        await user.getIdToken(true); // Force refresh
        console.log('✅ Token refreshed after coming online');
      } catch (error) {
        console.warn('⚠️ Token refresh failed:', error.message);
      }
    }
  });

  // Refresh token when app becomes visible again (user switches back to app)
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible') {
      const user = auth.currentUser;
      if (user) {
        try {
          await user.getIdToken(true); // Force refresh
          console.log('✅ Token refreshed on app visibility');
        } catch (error) {
          console.warn('⚠️ Token refresh on visibility failed:', error.message);
        }
      }
    }
  });

  // Periodic token refresh every 30 minutes to stay ahead of expiry
  setInterval(async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        await user.getIdToken(true);
        console.log('✅ Periodic token refresh successful');
      } catch (error) {
        console.warn('⚠️ Periodic token refresh failed:', error.message);
      }
    }
  }, 30 * 60 * 1000); // 30 minutes
};

// Initialize token refresh handling
if (typeof window !== 'undefined') {
  setupTokenRefresh();
}

// 🔔 Firebase Cloud Messaging (FCM) for push notifications
export const getMessagingInstance = async () => {
  try {
    const supported = await isSupported();
    if (supported) {
      return getMessaging(app);
    }
    return null;
  } catch (error) {
    console.error('Messaging not supported:', error);
    return null;
  }
};

// 🛡️ Auto-create user doc if missing
onAuthStateChanged(auth, async (user) => {
  if (!user) return;

  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);

  if (!snap.exists()) {
    await setDoc(userRef, {
      email: user.email,
      displayName: user.displayName || "Anonymous",
      isAdmin: false // Customize this if needed
    });
    console.log(`✅ Created user document for ${user.email}`);
  }
});

export { signInWithRedirect };
